import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useEffect, useMemo, useRef, useState } from "react";
import reportsService, { SsrsParameterDefinition, SsrsReportInfo } from "services/reportsService";
import { looksLikeDateParam, outputFormatFor, parseParamDate } from "../dateParams";

const DOWNLOAD_FORMATS: { label: string; format: string; ext: string }[] = [
  { label: "PDF", format: "PDF", ext: "pdf" },
  { label: "Excel", format: "EXCELOPENXML", ext: "xlsx" },
  { label: "CSV", format: "CSV", ext: "csv" },
  { label: "Word", format: "WORDOPENXML", ext: "docx" },
];

// Oversized landscape page (24x12) with near-zero margins — gives SSRS the most
// horizontal room possible at the URL Access layer so wide tables don't overflow onto
// continuation pages. The browser PDF viewer's FitH then scales the resulting wide PDF
// down to fit the iframe, producing a "shrink to fit" effect for the user.
// SSRS PDF render has no content-scale device info; physical page width is the only knob.
const LANDSCAPE_DEVICE_INFO: Record<string, string> = {
  "rc:PageWidth": "30in",
  "rc:PageHeight": "11in",
  "rc:MarginLeft": "0in",
  "rc:MarginRight": "0in",
  "rc:MarginTop": "0.25in",
  "rc:MarginBottom": "0.25in",
};

// Surface the backend's rich SSRS diagnostic (returned as { error } by the API's global
// handler) straight to the UI, and mirror the full error object to the browser console.
// For JSON endpoints axios puts the parsed body on response.data; statusText/message are
// last-resort fallbacks.
const ssrsErrorText = (e: any, fallback: string): string => {
  // eslint-disable-next-line no-console
  console.error("[Reports]", fallback, e?.response?.status, e?.response?.data, e);
  return e?.response?.data?.error || e?.response?.statusText || e?.message || fallback;
};

// A parameter value is a single string (single-value params) or an array (multi-value params).
type ParamValue = string | string[];

const asArray = (v: ParamValue | undefined): string[] =>
  v === undefined ? [] : Array.isArray(v) ? v.filter((x) => x !== "") : v === "" ? [] : [v];

const isFilled = (v: ParamValue | undefined): boolean => asArray(v).length > 0;

const ReportsDashboard = () => {
  const [reports, setReports] = useState<SsrsReportInfo[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selected, setSelected] = useState<SsrsReportInfo | null>(null);
  const [paramDefs, setParamDefs] = useState<SsrsParameterDefinition[] | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, ParamValue>>({});
  const [paramsLoading, setParamsLoading] = useState(false);
  const [paramError, setParamError] = useState<string | null>(null);

  const [downloading, setDownloading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [paramsRefreshing, setParamsRefreshing] = useState(false);
  // The parameter the user last edited. The cascade only fires when an *upstream* parameter
  // (one that some other parameter's `dependencies` references) changes — never on initial
  // default-population, and never on a leaf param like start/end/dispatcher.
  const lastEditedParamRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    reportsService
      .listReports()
      .then((r) => !cancelled && setReports(r))
      .catch((e) => !cancelled && setLoadError(ssrsErrorText(e, "Failed to load reports.")));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    lastEditedParamRef.current = null;
    setParamDefs(null);
    setParamValues({});
    setParamError(null);
    setActionError(null);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (!selected) return;
    let cancelled = false;
    setParamsLoading(true);
    reportsService
      .getParameters(selected.id)
      .then((defs) => {
        if (cancelled) return;
        setParamDefs(defs);
        const initial: Record<string, ParamValue> = {};
        defs.forEach((d) => {
          initial[d.name] = d.multiValue ? d.defaultValues ?? [] : (d.defaultValues && d.defaultValues[0]) ?? "";
        });
        setParamValues(initial);
      })
      .catch((e) => !cancelled && setParamError(ssrsErrorText(e, "Failed to load parameters.")))
      .finally(() => !cancelled && setParamsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selected?.id]);

  // Cascading parameters: when an upstream value changes, ask SSRS to recompute the
  // ValidValues for every parameter. Debounced so we don't hammer SSRS while the user
  // is still typing a date or switching dropdowns rapidly. We preserve user-entered
  // values where they remain valid against the new ValidValues set.
  useEffect(() => {
    if (!selected?.id || !paramDefs) return;
    // Only cascade when the user edited an *upstream* parameter — one that some other
    // parameter declares in its `dependencies`. Skips initial default-population (no edit yet)
    // and leaf-parameter edits (start/end/dispatcher) that can't change anyone's ValidValues.
    const edited = lastEditedParamRef.current;
    if (!edited) return;
    const upstreamParams = new Set(paramDefs.flatMap((d) => d.dependencies ?? []));
    if (!upstreamParams.has(edited)) return;
    const filled = Object.entries(paramValues).filter(([, v]) => isFilled(v));
    if (filled.length === 0) return;

    let cancelled = false;
    const handle = setTimeout(() => {
      setParamsRefreshing(true);
      reportsService
        .getParametersWithValues(
          selected.path,
          Object.fromEntries(filled.map(([k, v]) => [k, asArray(v)]))
        )
        .then((defs) => {
          if (cancelled) return;
          setParamDefs(defs);
          setParamValues((prev) => {
            const next: Record<string, ParamValue> = {};
            defs.forEach((d) => {
              const current = prev[d.name];
              const hasValid = !!d.validValues && d.validValues.length > 0;
              if (d.multiValue) {
                // keep only previously-selected values that remain valid under new upstream values
                const curArr = asArray(current);
                if (hasValid) {
                  const valid = new Set(d.validValues!.map((v) => v.value));
                  const kept = curArr.filter((x) => valid.has(x));
                  next[d.name] = kept.length > 0 ? kept : d.defaultValues ?? [];
                } else {
                  next[d.name] = curArr;
                }
              } else {
                const cur = typeof current === "string" ? current : "";
                if (hasValid && cur && !d.validValues!.some((v) => v.value === cur)) {
                  next[d.name] = (d.defaultValues && d.defaultValues[0]) ?? "";
                } else if (cur === "" && d.defaultValues && d.defaultValues[0]) {
                  next[d.name] = d.defaultValues[0];
                } else {
                  next[d.name] = cur;
                }
              }
            });
            // Return the SAME reference when nothing changed, otherwise this setState produces a
            // new object every cascade and re-triggers this effect (which depends on paramValues),
            // looping every 250ms. Only emit a new object when a value actually differs.
            const sameVal = (a: ParamValue, b: ParamValue) =>
              Array.isArray(a) || Array.isArray(b)
                ? JSON.stringify(asArray(a)) === JSON.stringify(asArray(b))
                : a === b;
            const nextKeys = Object.keys(next);
            const unchanged =
              nextKeys.length === Object.keys(prev).length && nextKeys.every((k) => sameVal(prev[k], next[k]));
            return unchanged ? prev : next;
          });
        })
        .catch((e) => !cancelled && setParamError(ssrsErrorText(e, "Failed to refresh parameters.")))
        .finally(() => !cancelled && setParamsRefreshing(false));
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // paramDefs is intentionally excluded — we only want to refresh when the user
    // changes a value, not when this effect's own setParamDefs fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramValues, selected?.id]);

  // SSRS requires a value whenever Nullable is false (a param with a default is satisfied by it).
  // AllowBlank only controls whether an empty string is a valid value for string params — it does
  // NOT make the param optional, so it must not factor into "required".
  const requiredParams = useMemo(() => (paramDefs ?? []).filter((d) => !d.nullable), [paramDefs]);
  const canRun = useMemo(() => {
    if (!selected || !paramDefs) return false;
    return requiredParams.every((d) => isFilled(paramValues[d.name]));
  }, [selected, paramDefs, requiredParams, paramValues]);

  const buildParamMap = (): Record<string, string[]> => {
    const params: Record<string, string[]> = {};
    (paramDefs ?? []).forEach((d) => {
      const arr = asArray(paramValues[d.name]);
      if (arr.length > 0) params[d.name] = arr;
    });
    return params;
  };

  // Device-info params (rc:PageWidth etc.) are single-valued; wrap each as a one-element array
  // so they fit the multi-value parameters contract.
  const deviceInfoParams: Record<string, string[]> = Object.fromEntries(
    Object.entries(LANDSCAPE_DEVICE_INFO).map(([k, v]) => [k, [v]])
  );

  const runReport = async () => {
    if (!selected) return;
    setRendering(true);
    setActionError(null);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const blob = await reportsService.renderReport({
        reportPath: selected.path,
        format: "PDF",
        parameters: { ...buildParamMap(), ...deviceInfoParams },
      });
      setPdfUrl(URL.createObjectURL(blob));
    } catch (e: any) {
      const blob = e?.response?.data as Blob | undefined;
      let detail: string | undefined;
      if (blob && typeof blob.text === "function") {
        try {
          const text = await blob.text();
          try {
            detail = JSON.parse(text)?.error ?? text;
          } catch {
            detail = text;
          }
        } catch {
          // ignore
        }
      }
      // eslint-disable-next-line no-console
      console.error("[Reports] render failed", e?.response?.status, detail, e);
      setActionError(detail || e?.response?.statusText || e?.message || "Failed to render report.");
    } finally {
      setRendering(false);
    }
  };

  const downloadAs = async (format: string, ext: string) => {
    if (!selected) return;
    setDownloading(format);
    setActionError(null);
    try {
      const blob = await reportsService.renderReport({
        reportPath: selected.path,
        format,
        parameters: buildParamMap(),
      });
      const safeName = selected.name.replace(/[^A-Za-z0-9._-]+/g, "_");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      const blob = e?.response?.data as Blob | undefined;
      let detail: string | undefined;
      if (blob && typeof blob.text === "function") {
        try {
          const text = await blob.text();
          try {
            detail = JSON.parse(text)?.error ?? text;
          } catch {
            detail = text;
          }
        } catch {
          // ignore
        }
      }
      // eslint-disable-next-line no-console
      console.error("[Reports] download failed", e?.response?.status, detail, e);
      setActionError(detail || e?.response?.statusText || e?.message || `Failed to download as ${format}.`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="calc(100vh - 96px)" p={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} mb={2}>
        <Autocomplete
          sx={{ flex: 1, minWidth: 320 }}
          options={reports ?? []}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          loading={!reports && !loadError}
          renderInput={(params) => (
            <TextField {...params} label="Report" placeholder="Select a report..." size="small" />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.path}
                </Typography>
              </Box>
            </li>
          )}
        />
        {selected && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={rendering ? <CircularProgress size={16} sx={{ color: "common.white" }} /> : <PlayArrowIcon />}
              onClick={runReport}
              disabled={!canRun || rendering}
              sx={{ color: "common.white" }}
            >
              Run
            </Button>
            {DOWNLOAD_FORMATS.map((f) => (
              <Button
                key={f.format}
                variant="contained"
                color="primary"
                size="small"
                startIcon={downloading === f.format ? <CircularProgress size={16} sx={{ color: "common.white" }} /> : undefined}
                disabled={!canRun || !!downloading}
                onClick={() => downloadAs(f.format, f.ext)}
                sx={{ color: "common.white" }}
              >
                {f.label}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>

      {loadError && (
        <Typography color="error" mb={2} sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {loadError}
        </Typography>
      )}

      {selected && paramsLoading && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading parameters...
          </Typography>
        </Box>
      )}

      {paramError && (
        <Typography color="error" mb={2} sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {paramError}
        </Typography>
      )}

      {paramDefs && paramDefs.length > 0 && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mb={2} alignItems="center">
          {paramsRefreshing && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Refreshing...</Typography>
            </Stack>
          )}
          {paramDefs.map((d) => {
            const hasValid = !!d.validValues && d.validValues.length > 0;
            const isBool = (d.parameterTypeName ?? "").toLowerCase() === "boolean";
            const firstDefault = d.defaultValues && d.defaultValues[0];
            // parameterTypeName is often absent; fall back to detecting an ISO datetime default.
            const isDate =
              (d.parameterTypeName ?? "").toLowerCase() === "datetime" ||
              (!!firstDefault && /^\d{4}-\d{2}-\d{2}T/.test(firstDefault)) ||
              // Reports that declare Start Date / End Date as String reach the calendar by label.
              looksLikeDateParam(d.name, d.prompt);
            const required = !d.nullable;
            const label = `${d.prompt || d.name}${required ? " *" : ""}`;
            const curString = typeof paramValues[d.name] === "string" ? (paramValues[d.name] as string) : "";
            const markEdited = () => {
              lastEditedParamRef.current = d.name;
            };

            // Multi-value parameter: chip multi-select. Options come from validValues, which stay
            // empty until a dependency is selected and the cascade fills them in. When the option
            // list is empty AND there's no upstream dependency to wait on, fall back to freeSolo
            // so the user can still type values manually — matching Dashboard's native SSRS behavior
            // (e.g. the TripCancelStatus report whose TripIDs parameter ships with no value list).
            if (d.multiValue) {
              const options = d.validValues ?? [];
              const selectedValues = asArray(paramValues[d.name]);
              const dependsOn = d.dependencies && d.dependencies.length > 0 ? d.dependencies.join(", ") : null;
              const allowFreeText = options.length === 0 && !dependsOn;
              const selectedAsObjects = allowFreeText
                ? selectedValues.map((v) => ({ label: v, value: v }))
                : options.filter((o) => selectedValues.includes(o.value));
              return (
                <Autocomplete
                  key={d.name}
                  multiple
                  freeSolo={allowFreeText}
                  size="small"
                  sx={{ minWidth: 280, maxWidth: 520 }}
                  options={options}
                  getOptionLabel={(o) => (typeof o === "string" ? o : o.label || o.value)}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={selectedAsObjects}
                  onChange={(_, newVal) => {
                    markEdited();
                    const valuesArr = (newVal as any[]).map((v) =>
                      typeof v === "string" ? v : v.value
                    );
                    setParamValues((prev) => ({ ...prev, [d.name]: valuesArr }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={label}
                      placeholder={
                        options.length === 0 && dependsOn
                          ? `Select ${dependsOn} first`
                          : selectedValues.length
                          ? ""
                          : allowFreeText
                          ? "Type a value and press Enter"
                          : "Any"
                      }
                    />
                  )}
                />
              );
            }

            // Single-value parameter with a value list: searchable single-select. If validValues
            // exists but is empty (rare — usually means the data source returned no rows), let
            // the user type a value just like Dashboard's SSRS UI does.
            if (hasValid) {
              return (
                <Autocomplete
                  key={d.name}
                  size="small"
                  sx={{ minWidth: 240 }}
                  options={d.validValues ?? []}
                  getOptionLabel={(o) => (typeof o === "string" ? o : o.label || o.value)}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={(d.validValues ?? []).find((o) => o.value === curString) ?? null}
                  onChange={(_, newVal) => {
                    markEdited();
                    setParamValues((prev) => ({
                      ...prev,
                      [d.name]: typeof newVal === "string" ? newVal : newVal ? newVal.value : "",
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label={label} />}
                />
              );
            }

            if (isBool) {
              return (
                <TextField
                  key={d.name}
                  select
                  label={label}
                  value={curString}
                  onChange={(e) => {
                    markEdited();
                    setParamValues((prev) => ({ ...prev, [d.name]: e.target.value }));
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="true">True</MenuItem>
                  <MenuItem value="false">False</MenuItem>
                </TextField>
              );
            }

            // A date parameter gets a real calendar. Guarded on the value actually being parseable:
            // if SSRS hands back a default in some shape we do not recognise, fall through to the
            // text field so the value stays visible and editable instead of silently vanishing into
            // an empty picker the user cannot correct.
            if (isDate && (!curString || parseParamDate(curString))) {
              return (
                <DatePicker
                  key={d.name}
                  label={label}
                  value={parseParamDate(curString)}
                  onChange={(value) => {
                    markEdited();
                    setParamValues((prev) => ({
                      ...prev,
                      [d.name]: value && value.isValid() ? value.format(outputFormatFor(firstDefault)) : "",
                    }));
                  }}
                  slotProps={{
                    textField: { size: "small", sx: { minWidth: 200 } },
                    // Optional parameters need a way back to empty; required ones do not offer it.
                    field: { clearable: d.nullable },
                  }}
                />
              );
            }

            return (
              <TextField
                key={d.name}
                label={label}
                value={curString}
                onChange={(e) => {
                  markEdited();
                  setParamValues((prev) => ({ ...prev, [d.name]: e.target.value }));
                }}
                size="small"
                sx={{ minWidth: 200 }}
              />
            );
          })}
        </Stack>
      )}

      {actionError && (
        <Typography color="error" mb={2} sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {actionError}
        </Typography>
      )}

      <Box flex={1} sx={{ border: 1, borderColor: "divider", borderRadius: 1, position: "relative", overflow: "hidden" }}>
        {!selected && (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%" px={3}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Pick a report from the dropdown to get started.
            </Typography>
          </Box>
        )}
        {selected && rendering && (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <CircularProgress />
          </Box>
        )}
        {selected && !rendering && !pdfUrl && (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%" px={3}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {canRun ? "Click Run to render the report." : "Fill in the required parameters above."}
            </Typography>
          </Box>
        )}
        {pdfUrl && !rendering && (
          <iframe
            title={selected?.name ?? "Report"}
            src={`${pdfUrl}#view=FitH`}
            style={{ width: "100%", height: "100%", border: 0, background: "white" }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ReportsDashboard;
